/**
 * Accept Visit Page
 *
 * Public page that allows agents to accept visit requests via a link.
 * No authentication required for preferred agents.
 */

import {
  Banner,
  Button,
  DescriptionList,
  formatDate,
  Heading,
  Progress,
  SecondaryButton,
  Section,
  useRequest,
} from '@ox/blox';
import { ResponseError } from '@ox/ox-brokerage-client';
import type { AcceptVisitRequestResponse } from '@ox/ox-brokerage-client';
import { visitRequestsApi } from '@ox/visits-ui/clients';
import type { FunctionalComponent } from 'preact';
import { useRoute } from 'preact-iso';
import { useCallback, useEffect, useState } from 'preact/hooks';

/**
 * Loading spinner component
 */
const LoadingSpinner: FunctionalComponent = () => (
  <div className="accept-visit-loading">
    <Progress label="Accepting visit request..." labelPosition="after" type="circular" />
  </div>
);

/** `error_code` the backend puts on the 409 for a request another agent already took (TXN-3420). */
const VISIT_TAKEN_ERROR_CODE = 'visit_taken';

const TAKEN_BODY =
  'Another agent accepted this visit request before you. Thanks for jumping on it, and keep an eye out for the next one.';

/** True when the failed call was the backend's "another agent took this visit" 409. */
const isVisitTakenError = async (error: unknown): Promise<boolean> => {
  if (!(error instanceof ResponseError) || error.response.status !== 409) {
    return false;
  }
  try {
    const body = (await error.response.clone().json()) as { detail?: { error_code?: string } };
    return body?.detail?.error_code === VISIT_TAKEN_ERROR_CODE;
  } catch {
    return false;
  }
};

export const AcceptVisitPage: FunctionalComponent = () => {
  const { params } = useRoute();
  const {
    request,
    isPending: loading,
    results,
  } = useRequest({
    context: visitRequestsApi,
    method: visitRequestsApi.acceptVisitRequestApiV1VisitRequestsVisitRequestAcceptPatch,
    params: { visitRequestId: params.visitRequestId as string },
  });
  const [userAction, setUserAction] = useState<'pending' | 'accepted' | 'declined'>('pending');
  const {
    request: listingAddressRequest,
    isPending: loadingAddress,
    results: listingAddressResults,
  } = useRequest({
    context: visitRequestsApi,
    method: visitRequestsApi.getListingAddressApiV1VisitRequestsVisitRequestIdListingAddressGet,
    params: { visitRequestId: params.visitRequestId as string },
  });
  const { request: rejectRequest } = useRequest({
    context: visitRequestsApi,
    method: visitRequestsApi.markVisitRequestAsRejectedApiV1VisitRequestsVisitRequestIdRejectPatch,
    params: { visitRequestId: params.visitRequestId as string },
  });

  const acceptVisit = useCallback(async () => {
    setUserAction('accepted');
    await request({ visitRequest: params.visitRequestId as string });
  }, [request, params.visitRequestId]);

  const declineVisit = useCallback(() => {
    setUserAction('declined');
    rejectRequest({ visitRequestId: params.visitRequestId as string });
  }, [params.visitRequestId, rejectRequest]);

  const visitResponse: AcceptVisitRequestResponse = results.data as
    | AcceptVisitRequestResponse
    | undefined;

  const listingAddressSet = listingAddressResults.data;
  const addresses = listingAddressSet ? listingAddressSet.split('\n') : [];
  const addressError = listingAddressResults.error;

  // Check for HTTP errors (400, 404, etc.) or response status errors
  const hasError = results.error || visitResponse?.status === 'error' || addressError;

  // Why the request is unavailable: `undefined` while the failed response is being read.
  const [taken, setTaken] = useState<boolean | undefined>(undefined);
  const failure = results.error ?? addressError;
  useEffect(() => {
    if (!failure) {
      setTaken(undefined);
      return;
    }
    let cancelled = false;
    void isVisitTakenError(failure).then((value) => {
      if (!cancelled) {
        setTaken(value);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [failure]);

  useEffect(() => {
    listingAddressRequest({ visitRequestId: params.visitRequestId as string });
  }, [params.visitRequestId, listingAddressRequest]);

  // Show loading spinner only when accepting
  if (loadingAddress || (loading && userAction === 'accepted')) {
    return <LoadingSpinner />;
  }

  // An HTTP failure's body is read asynchronously to tell "taken" from other causes.
  if (failure && userAction !== 'declined' && taken === undefined) {
    return <LoadingSpinner />;
  }

  // Show confirmation UI if user hasn't taken action yet
  if (userAction === 'pending' && !addressError) {
    return (
      <div className="accept-visit-page">
        <Section type="bounded" heading="Visit Request">
          <p>You have been invited to accept a visit request.</p>
          <p>
            <strong>Listing address{addresses.length > 1 && 'es'}:</strong>
          </p>
          <ul className="address-list">
            {addresses.map((address) => (
              <li key={address}>{address}</li>
            ))}
          </ul>
          <div className="accept-buttons-wrapper">
            <SecondaryButton onClick={declineVisit}>Reject</SecondaryButton>
            <Button onClick={acceptVisit}>Accept</Button>
          </div>
        </Section>
      </div>
    );
  }

  // Show declined message
  if (userAction === 'declined') {
    return (
      <div className="accept-visit-page">
        <Section type="bounded">
          <Banner theme="positive">Visit request declined</Banner>
          <p>You have declined this visit request. No more actions needed.</p>
        </Section>
      </div>
    );
  }

  // Show acceptance result (success or error)
  let bannerText = 'Visit accepted successfully';
  let bodyText = 'This visit request may have already been accepted or is no longer pending.';
  if (taken) {
    bannerText = 'Sorry, this visit was just taken';
    bodyText = TAKEN_BODY;
  } else if (hasError) {
    bannerText = 'This visit request is no longer available';
  }
  return (
    <div className="accept-visit-page">
      <Section type="bounded">
        <Banner theme={hasError ? 'negative' : 'positive'}>{bannerText}</Banner>

        {hasError && (
          <div>
            <p>{bodyText}</p>
          </div>
        )}

        {visitResponse?.updatedVisit && !hasError && (
          <div className="visit-details">
            <Heading level="h1">Visit Details</Heading>
            <DescriptionList
              termTypography="label-small"
              descriptionTypography="label-small"
              orientation="vertical"
              items={{
                Address: (
                  <ul className="address-list">
                    {addresses.map((address) => (
                      <li key={address}>{address}</li>
                    ))}
                  </ul>
                ),
                'Visit Type': visitResponse.updatedVisit.visitType,
                Notes: visitResponse.updatedVisit.notes || 'No notes provided',
                Status: visitResponse.updatedVisit.status,
                'Claimed at': visitResponse.updatedVisit.completedAt
                  ? formatDate(visitResponse.updatedVisit.completedAt, 'MM/DD/YYYY, HH:MI TZ')
                  : 'Not completed yet',
              }}
            />
          </div>
        )}
      </Section>
    </div>
  );
};
